/**
 * PhoneBatchStatus — simple in-process pill
 *
 * Real-time listener on /clients/{id}/phoneBatchJobs for jobs in any active
 * status (uploaded_disconnect / uploaded_reverse / paid). Renders a small pill
 * indicating the current stage so the client knows verification is happening.
 *
 * Intentionally minimal — no counts, just stage. Hidden when no active jobs.
 */

import { useEffect, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../services/firebase';

const ACTIVE_STATUSES = ['paid', 'uploaded_disconnect', 'uploaded_reverse'];

interface Props {
  clientId: string | null;
}

interface ActiveJob {
  jobId: string;
  status: string;
  stage?: string;  // 'disconnect' | 'reverse'
}

export function PhoneBatchStatus({ clientId }: Props) {
  const [activeJobs, setActiveJobs] = useState<ActiveJob[]>([]);

  useEffect(() => {
    if (!clientId) return;
    // Listen to all jobs in active statuses
    const q = query(
      collection(db, 'clients', clientId, 'phoneBatchJobs'),
      where('status', 'in', ACTIVE_STATUSES)
    );
    const unsub = onSnapshot(q, snap => {
      const jobs: ActiveJob[] = [];
      snap.forEach(doc => {
        const d = doc.data();
        jobs.push({
          jobId: doc.id,
          status: d.status as string,
          stage: d.stage as string | undefined,
        });
      });
      setActiveJobs(jobs);
    });
    return () => unsub();
  }, [clientId]);

  if (activeJobs.length === 0) return null;

  // If any job is in the reverse stage, prefer that label since it implies
  // disconnect already finished. Otherwise show disconnect.
  const inReverse = activeJobs.some(j => j.stage === 'reverse' || j.status === 'uploaded_reverse');
  const label = inReverse ? 'Reverse Phone in process' : 'Phone Disconnect in process';

  return (
    <div style={pillStyle}>
      <span style={pulseStyle}>●</span>
      <span style={{ marginLeft: '8px' }}>{label}</span>
    </div>
  );
}

const pillStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '6px 12px',
  borderRadius: '999px',
  background: 'rgba(200, 168, 78, 0.12)',
  border: '1px solid rgba(200, 168, 78, 0.4)',
  color: 'var(--gold)',
  fontSize: '0.8rem',
  fontWeight: 600,
  letterSpacing: '0.02em',
};

const pulseStyle: React.CSSProperties = {
  display: 'inline-block',
  color: 'var(--gold)',
  animation: 'pulse 1.4s infinite',
  fontSize: '0.7rem',
};
